/**
 * Updates the recently viewed products in localStorage.
 */
export class RecentlyViewed {
  /** @static @constant {string} The key used to store the viewed products */
  static #STORAGE_KEY = 'viewedProducts';

  /** @static @constant {number} The maximum number of products to store */
  static #MAX_PRODUCTS = 4;

  /**
   * Adds a product to the recently viewed products list.
   * Products viewed inside the Shopify theme editor are ignored.
   *
   * @param {string} productId - The ID of the product to add.
   */
  static addProduct(productId) {
    // Don't record products while using Shopify's theme editor.
    if (window.Shopify && window.Shopify.designMode) {
      return;
    }

    // Ignore empty or invalid IDs.
    if (!productId || productId === '0' || productId === 'undefined' || productId === 'null') {
      return;
    }

    // Always work with a string ID.
    productId = String(productId).trim();

    if (!productId) {
      return;
    }

    let viewedProducts = this.getProducts();

    // Remove duplicates.
    viewedProducts = viewedProducts.filter(
      (/** @type {string} */ id) => String(id) !== productId
    );

    // Put newest product first.
    viewedProducts.unshift(productId);

    // Keep only the most recent products.
    viewedProducts = viewedProducts.slice(0, this.#MAX_PRODUCTS);

    localStorage.setItem(
      this.#STORAGE_KEY,
      JSON.stringify(viewedProducts)
    );
  }

  /**
   * Removes a specific product from recently viewed.
   *
   * @param {string} productId - Product ID to remove.
   */
  static removeProduct(productId) {
    if (!productId) return;

    const viewedProducts = this.getProducts().filter(
      (/** @type {string} */ id) => String(id) !== String(productId)
    );

    localStorage.setItem(
      this.#STORAGE_KEY,
      JSON.stringify(viewedProducts)
    );
  }

  /**
   * Clears the entire recently viewed list.
   */
  static clearProducts() {
    localStorage.removeItem(this.#STORAGE_KEY);
  }

  /**
   * Retrieves recently viewed product IDs.
   *
   * @returns {string[]} The list of viewed products.
   */
  static getProducts() {
    try {
      const stored = JSON.parse(
        localStorage.getItem(this.#STORAGE_KEY) || '[]'
      );

      if (!Array.isArray(stored)) {
        return [];
      }

      return stored
        .filter((id) => id !== null && id !== undefined)
        .map((id) => String(id).trim())
        .filter((id) => id.length > 0);
    } catch (error) {
      console.warn('Could not read recently viewed products:', error);

      localStorage.removeItem(this.#STORAGE_KEY);
      return [];
    }
  }
}